import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentAbilityKey,
} from 'app/frontend/types/custom/RaidPlan'
import { useTeam } from 'app/frontend/base/config/context/team'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { getClass } from 'app/frontend/base/services/characterValues'
import { v4 as uuid } from 'uuid'
import {
  getEffectiveAbilityCustomGrouping,
  normalizeAssignmentCast,
} from './assignmentTimeline'
import { getEncounterAbilityShowByDefault } from 'app/frontend/types/custom/EncounterAbilityAssignmentDefaults'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import SpellIcon from './SpellIcon'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import TeamPlanAssignmentRow from './TeamPlanAssignmentRow'
import {
  AssignmentPaletteEntity,
  buildAssignmentPaletteEntities,
} from './assignmentPaletteEntities'

const isStandardAbilityKey = (abilityKey: string) => /^\d+$/.test(abilityKey)

const TeamPlanAssignments = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const {
    timeline,
    computed,
    canEdit,
    updateTimeline,
    setAssignmentDialogOpen,
  } = useTeamPlanTimeline()
  const [openRaidPlanAbilityKey, setOpenRaidPlanAbilityKey] = useState<
    string | null
  >(null)
  const [confirmRemoveAbilityKey, setConfirmRemoveAbilityKey] = useState<
    string | null
  >(null)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
  )

  const encounterRowBySpellId = useMemo(
    () =>
      computed.encounterAbilityRows.reduce<
        Record<number, typeof computed.encounterAbilityRows[number]>
      >((acc, row) => {
        acc[row.spellId] = row
        return acc
      }, {}),
    [computed.encounterAbilityRows],
  )

  const selectedSpellIds = useMemo(() => {
    const timelineSelectedSpellIds = Object.entries(
      timeline.encounter_assignments?.abilities ?? {},
    )
      .filter(([abilityKey]) => isStandardAbilityKey(abilityKey))
      .map(([, ability]) => ability.spell_id)
      .filter((spellId) => Boolean(encounterRowBySpellId[spellId]))
    const selected = new Set(timelineSelectedSpellIds)
    computed.encounterAbilityRows.forEach((row) => {
      if (!getEncounterAbilityShowByDefault(row.spellId)) return
      selected.add(row.spellId)
    })
    return [
      ...timelineSelectedSpellIds,
      ...computed.encounterAbilityRows
        .map((row) => row.spellId)
        .filter(
          (spellId) =>
            selected.has(spellId) &&
            !timelineSelectedSpellIds.includes(spellId),
        ),
    ]
  }, [computed.encounterAbilityRows, encounterRowBySpellId, timeline])

  const selectedRows = useMemo(() => {
    const filteredRows = computed.encounterAssignmentRows.filter((row) => {
      if (!isStandardAbilityKey(row.abilityKey)) return true
      if (selectedSpellIds.includes(row.spellId)) return true
      return getEncounterAbilityShowByDefault(row.spellId)
    })
    const abilityOrder = timeline.encounter_assignments?.ability_order ?? []
    const orderIndexByAbilityKey = abilityOrder.reduce<Record<string, number>>(
      (acc, abilityKey, index) => {
        acc[abilityKey] = index
        return acc
      },
      {},
    )
    return filteredRows.sort((left, right) => {
      const leftOrder = orderIndexByAbilityKey[left.abilityKey]
      const rightOrder = orderIndexByAbilityKey[right.abilityKey]
      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder
      }
      if (leftOrder !== undefined) return -1
      if (rightOrder !== undefined) return 1
      return left.first - right.first
    })
  }, [
    computed.encounterAssignmentRows,
    selectedSpellIds,
    timeline.encounter_assignments?.ability_order,
  ])

  const abilityOptions = useMemo(
    () =>
      computed.encounterAbilityRows
        .filter((row) => !selectedSpellIds.includes(row.spellId))
        .map((row) => ({
          spellId: row.spellId,
          label: row.name,
          color: row.color,
        })),
    [computed.encounterAbilityRows, selectedSpellIds],
  )

  const specializationById = useMemo(
    () =>
      computed.specializations.reduce<
        Record<
          number,
          {
            name: string
            icon: string
            classId: number
            className: string
          }
        >
      >((acc, specialization) => {
        acc[specialization.id] = {
          name: specialization.name,
          icon: specialization.icon,
          classId: specialization.classId,
          className: specialization.className,
        }
        return acc
      }, {}),
    [computed.specializations],
  )

  const memberInfoByCharacterId = useMemo(
    () =>
      (team.members ?? []).reduce<
        Record<number, { name: string; className: string }>
      >((acc, membership) => {
        const character = membership.characterReference
        acc[character.id] = {
          name: character.name,
          className: character.classId
            ? getClass(character.classId.toString()) ?? 'Unknown'
            : 'Unknown',
        }
        return acc
      }, {}),
    [team.members],
  )

  const paletteEntities = useMemo<AssignmentPaletteEntity[]>(
    () =>
      buildAssignmentPaletteEntities({
        t,
        members: team.members ?? [],
        specializationById,
        classColor: computed.classColor,
      }),
    [computed.classColor, specializationById, t, team.members],
  )
  const paletteLabelByIdentity = useMemo(
    () =>
      new Map(
        paletteEntities.map((entry) => [
          getAssignmentEntityIdentity(entry.entity),
          entry.label,
        ]),
      ),
    [paletteEntities],
  )

  const ensureAbilityOnTimeline = (
    abilitiesBySpellId: Record<string, RaidPlanEncounterAssignmentAbility>,
    abilityOrder: RaidPlanEncounterAssignmentAbilityKey[],
    abilityKey: RaidPlanEncounterAssignmentAbilityKey,
    spellId: number,
    castCount: number,
    assignmentGroups: number,
  ) => {
    const existingAbility = abilitiesBySpellId[abilityKey]
    if (existingAbility) {
      abilitiesBySpellId[abilityKey] = {
        ...existingAbility,
        casts: Array.from({ length: castCount }, (_, castIndex) =>
          normalizeAssignmentCast(
            existingAbility.casts?.[castIndex],
            assignmentGroups,
          ),
        ),
      }
      return abilitiesBySpellId[abilityKey]
    }
    const created = {
      spell_id: spellId,
      casts: Array.from({ length: castCount }, () =>
        normalizeAssignmentCast(undefined, assignmentGroups),
      ),
      assignment_groups: undefined,
      message: undefined,
      tts_message: undefined,
      cast_overrides: undefined,
      rgb: undefined,
      raid_plan_link: undefined,
      view_mode: undefined,
      custom_grouping: getEffectiveAbilityCustomGrouping(spellId),
      title: undefined,
    }
    abilitiesBySpellId[abilityKey] = created
    abilityOrder.push(abilityKey)
    return created
  }

  const updateAbility = (
    abilityKey: RaidPlanEncounterAssignmentAbilityKey,
    spellId: number,
    castCount: number,
    assignmentGroups: number,
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory = true,
  ) => {
    updateTimeline((next) => {
      if (!next.encounter_assignments) {
        next.encounter_assignments = { abilities: {}, ability_order: [] }
      }
      next.encounter_assignments.abilities =
        next.encounter_assignments.abilities ?? {}
      next.encounter_assignments.ability_order =
        next.encounter_assignments.ability_order ?? []
      const ability = ensureAbilityOnTimeline(
        next.encounter_assignments.abilities,
        next.encounter_assignments.ability_order,
        abilityKey,
        spellId,
        castCount,
        assignmentGroups,
      )
      updater(ability)
    }, pushHistory)
  }

  const addAbility = (spellId: number) => {
    const row = encounterRowBySpellId[spellId]
    if (!row) return
    const abilityKey = spellId.toString()
    updateAbility(
      abilityKey,
      spellId,
      row.entries.length,
      row.assignmentGroups,
      () => null,
    )
  }

  const addCustomAbility = () => {
    const abilityKey = uuid().replaceAll('-', '_')
    updateAbility(abilityKey, 136243, 0, 1, () => null)
    setOpenRaidPlanAbilityKey(abilityKey)
  }

  const removeAbility = (abilityKey: RaidPlanEncounterAssignmentAbilityKey) => {
    const spellId = Number(abilityKey)
    if (Number.isFinite(spellId) && getEncounterAbilityShowByDefault(spellId)) {
      return
    }
    updateTimeline((next) => {
      if (!next.encounter_assignments?.abilities) return
      delete next.encounter_assignments.abilities[abilityKey]
      next.encounter_assignments.ability_order =
        next.encounter_assignments.ability_order?.filter(
          (entryKey) => entryKey !== abilityKey,
        ) ?? []
    }, true)
  }

  const handleAbilityDragEnd = ({ active, over }: DragEndEvent) => {
    if (!canEdit) return
    if (!over) return
    const sourceIndex = selectedRows.findIndex(
      (row) => row.abilityKey === String(active.id),
    )
    const targetIndex = selectedRows.findIndex(
      (row) => row.abilityKey === String(over.id),
    )
    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      return
    }
    const reorderedAbilityKeys = arrayMove(
      selectedRows.map((row) => row.abilityKey),
      sourceIndex,
      targetIndex,
    )
    updateTimeline((next) => {
      if (!next.encounter_assignments) {
        next.encounter_assignments = { abilities: {}, ability_order: [] }
      }
      const existingOrder = next.encounter_assignments.ability_order ?? []
      const remainingOrder = existingOrder.filter(
        (abilityKey) => !reorderedAbilityKeys.includes(abilityKey),
      )
      next.encounter_assignments.ability_order = [
        ...reorderedAbilityKeys,
        ...remainingOrder,
      ]
    }, true)
  }

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center gap-2'></div>

      {selectedRows.length === 0 ? (
        <div className='py-8 text-sm text-muted-foreground text-center'>
          {t('dashboard.team.plans.assignments.empty')}
        </div>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleAbilityDragEnd}
      >
        <SortableContext
          items={selectedRows.map((row) => row.abilityKey)}
          strategy={verticalListSortingStrategy}
        >
          {selectedRows.map((row) => (
            <TeamPlanAssignmentRow
              key={row.abilityKey}
              row={row}
              t={t}
              canEdit={canEdit}
              openRaidPlanAbilityKey={openRaidPlanAbilityKey}
              setOpenRaidPlanAbilityKey={setOpenRaidPlanAbilityKey}
              setConfirmRemoveAbilityKey={setConfirmRemoveAbilityKey}
              setAssignmentDialogOpen={setAssignmentDialogOpen}
              paletteEntities={paletteEntities}
              paletteLabelByIdentity={paletteLabelByIdentity}
              memberInfoByCharacterId={memberInfoByCharacterId}
              specializationById={specializationById}
              storedAbility={
                timeline.encounter_assignments?.abilities?.[row.abilityKey]
              }
              updateAbility={updateAbility}
              onCommit={() => updateTimeline(() => null, true)}
              removeAbility={removeAbility}
            />
          ))}
        </SortableContext>
      </DndContext>

      {canEdit ? (
        <div className='flex justify-end'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type='button' variant='outline'>
                {t('dashboard.team.plans.assignments.add-ability')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {abilityOptions.map((option) => (
                <DropdownMenuItem
                  key={option.spellId}
                  onClick={() => addAbility(option.spellId)}
                >
                  <div className='flex items-center gap-2 min-w-0'>
                    <SpellIcon
                      spellId={option.spellId}
                      className='w-4 h-4 rounded border border-border shrink-0'
                    />
                    <span className='truncate' style={{ color: option.color }}>
                      {option.label}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem onClick={addCustomAbility}>
                <div className='flex items-center gap-2 min-w-0'>
                  <SpellIcon
                    spellId={136243}
                    className='w-4 h-4 rounded border border-border shrink-0'
                  />
                  <span className='truncate'>
                    {t('dashboard.team.plans.assignments.custom')}
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
      <ConfirmDialog
        open={Boolean(confirmRemoveAbilityKey)}
        descriptionKey='dashboard.team.plans.assignments.remove-ability-confirm'
        onCancel={() => setConfirmRemoveAbilityKey(null)}
        onContinue={() => {
          if (!confirmRemoveAbilityKey) return
          removeAbility(confirmRemoveAbilityKey)
          setConfirmRemoveAbilityKey(null)
        }}
        variant='destructive'
      />
    </div>
  )
}

export default TeamPlanAssignments
